import { VStack } from '@chakra-ui/layout';
import { Flex, Grid, Stack, Text, Image, Heading, SkeletonCircle, SkeletonText } from '@chakra-ui/react';
import { Response } from 'datx-jsonapi';
import { useResourcesInfinite } from 'datx-jsonapi-react';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InView } from 'react-intersection-observer';

import { NoResults } from '@/components/ui/shared/messages/NoResults/NoResults';
import { ProfilePopupNew } from '@/components/ui/shared/profile/ProfilePopup/ProfilePopupNew';
import { useAnalytics } from '@/context/Analytics';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { queryThinProfileInfinite } from '@/queries/thin-profiles';
import { ThinProfile } from '@/resources/ThinProfile';
import { extractSWRInfiniteData } from '@/utils/swr/extractSWRInfiniteData';

interface ILectureURLSearchParams {
  query?: string;
  eventId?: string;
}

const DEFAULT_PAGE_SIZE = 16;

const fallbackImageUrl = '/images/landing/conferences-gradient.png';

export const EventSpeakers: FC = () => {
  const { query, eventId } = useQueryParams<ILectureURLSearchParams>({
    query: StringParam,
    eventId: StringParam,
    searchGroup: StringParam,
  });
  const [popupProfile, setPopupProfile] = useState<null | ThinProfile>(null);

  const analytics = useAnalytics();
  const { t } = useTranslation();

  const filters = {
    event_id: eventId,
    query: query || '',
  };

  const { data, error, size, setSize } = useResourcesInfinite(queryThinProfileInfinite(filters, DEFAULT_PAGE_SIZE));

  const { items, isLoadingMore, isReachingEnd, isEmpty, meta } = useMemo(
    () => extractSWRInfiniteData<ThinProfile>(data as Array<Response<ThinProfile>>, error, size),
    [data, error, size]
  );
  const totalCount = meta?.totalCount;

  useEffect(() => {
    if (!meta || !filters.query) {
      return;
    }

    analytics.track('event_calendar_search', {
      event_id: eventId,
      query: filters.query,
      lectures_num: meta.totalCount,
    });
  }, [analytics, eventId, filters.query, meta]);

  if (isEmpty) {
    return (
      <VStack align={'start'} p={8}>
        <NoResults title={query ? t('common.noResultsForQuery', { query }) : t('common.noResults')} />
        <Text textStyle="plexBody">{t('schedule.search.notFound', { searchGroup: 'Authors' })}</Text>
      </VStack>
    );
  }

  return (
    <Stack spacing="20px" p={6}>
      <ProfilePopupNew isOpen={Boolean(popupProfile)} onClose={() => setPopupProfile(null)} profiles={[popupProfile]} />
      {!isLoadingMore && <Text textStyle="plexBody">{totalCount} results</Text>}
      <Grid
        gridTemplateColumns="repeat(auto-fill, min(100%, 290px))"
        justifyContent={{ base: 'center', lg: 'flex-start' }}
        gap="6"
      >
        {items.map((profile) => (
          <Flex
            w="288px"
            onClick={() => setPopupProfile(profile)}
            key={profile.id}
            textAlign="center"
            alignItems="center"
            p="4"
            flexDir="column"
            cursor="pointer"
            boxSizing="border-box"
            _hover={{
              boxShadow: 'card2xl',
            }}
          >
            <Image
              borderRadius="full"
              width="200px"
              height="200px"
              src={profile.mediumSquareAvatarUrl || fallbackImageUrl}
              alt={profile.firstName}
              objectFit="cover"
            />
            <Heading fontSize="3xl" mt="6">
              {`${profile.firstName} ${profile.lastName}`}
            </Heading>
            <Text fontSize="sm" noOfLines={3} color="gray.800" mt="2">
              {profile.title}
            </Text>
          </Flex>
        ))}
        {isLoadingMore &&
          Array.from({ length: DEFAULT_PAGE_SIZE }, (_, index) => (
            <Flex key={index} flexDir="column" h="full" alignItems="center" p="4" justifyContent="space-between">
              <SkeletonCircle size="200" />
              <SkeletonText w="full" mt="4" skeletonHeight="8" noOfLines={1} />
              <SkeletonText w="full" my="4" skeletonHeight="4" noOfLines={1} />
            </Flex>
          ))}
      </Grid>
      <InView
        onChange={(inView) => {
          if (inView && !error && !isReachingEnd) {
            setSize(size + 1);
          }
        }}
      />
    </Stack>
  );
};
